import { formatArea, type AreaUnit, type PlanOpening, type PlanRoom, type PlanWall, type TourLanguage, type Vec2 } from "@pisorama/shared";

/** Plan y is ARKit world Z; a Three box's local +X rotates toward +Z at -yaw. */
export function wallBox(wall: PlanWall) {
  const dx = wall.b.x - wall.a.x;
  const dz = wall.b.y - wall.a.y;
  const length = Math.hypot(dx, dz);
  if (!Number.isFinite(length) || length < 0.01) return null;
  const height = wall.height ?? 2.5;
  const thickness = wall.thickness ?? 0.12;
  return {
    center: [
      (wall.a.x + wall.b.x) / 2,
      height / 2,
      (wall.a.y + wall.b.y) / 2,
    ] as const,
    size: [length, height, thickness] as const,
    rotationY: -Math.atan2(dz, dx),
  };
}

export function modelBounds(walls: PlanWall[], positions: Vec2[]) {
  const points = [...walls.flatMap((w) => [w.a, w.b]), ...positions];
  const xs = points.map((p) => p.x);
  const zs = points.map((p) => p.y);
  const minX = Math.min(...xs),
    maxX = Math.max(...xs);
  const minZ = Math.min(...zs),
    maxZ = Math.max(...zs);
  return {
    center: [(minX + maxX) / 2, 0, (minZ + maxZ) / 2] as const,
    span: Math.max(maxX - minX, maxZ - minZ, 3),
  };
}

/**
 * Maqueta walls are cut away at this height so you can look into every room, like a
 * dollhouse. Plan readings carry no heights, so the model never shows real heights.
 */
export const CUT_HEIGHT = 1.1;
/** A window piece keeps a solid sill up to here; the glass runs to CUT_HEIGHT. */
export const SILL_HEIGHT = 0.8;
/** An opening this far (m) off a wall's line doesn't belong to that wall. */
const ON_WALL_TOLERANCE = 0.08;

export interface WallPiece {
  wall: PlanWall;
  kind: "solid" | "window";
}

/**
 * Split each wall into solid and window pieces, leaving a gap at every door. Openings
 * attach to a wall when both ends lie on its line (within ON_WALL_TOLERANCE) and
 * inside its span. Overlapping openings merge; a door wins over a window.
 */
export function wallPieces(walls: PlanWall[], openings: PlanOpening[]): WallPiece[] {
  const out: WallPiece[] = [];
  for (const wall of walls) {
    const dx = wall.b.x - wall.a.x;
    const dz = wall.b.y - wall.a.y;
    const length = Math.hypot(dx, dz);
    if (!Number.isFinite(length) || length < 0.01) continue;
    const along = (p: Vec2) => ((p.x - wall.a.x) * dx + (p.y - wall.a.y) * dz) / (length * length);
    const off = (p: Vec2) => Math.abs((p.x - wall.a.x) * dz - (p.y - wall.a.y) * dx) / length;
    const marks: Array<{ t0: number; t1: number; kind: PlanOpening["kind"] }> = [];
    for (const o of openings) {
      if (off(o.a) > ON_WALL_TOLERANCE || off(o.b) > ON_WALL_TOLERANCE) continue;
      const t0 = Math.max(0, Math.min(along(o.a), along(o.b)));
      const t1 = Math.min(1, Math.max(along(o.a), along(o.b)));
      if (t1 - t0 > 1e-3) marks.push({ t0, t1, kind: o.kind });
    }
    // Walk the wall in 0..1, classifying each stretch between consecutive breakpoints.
    const cuts = [...new Set([0, 1, ...marks.flatMap((m) => [m.t0, m.t1])])].sort((p, q) => p - q);
    const at = (t: number): Vec2 => ({ x: wall.a.x + dx * t, y: wall.a.y + dz * t });
    let run: { t0: number; kind: WallPiece["kind"] | "door" } | null = null;
    const flush = (t1: number) => {
      if (run && run.kind !== "door" && t1 - run.t0 > 1e-3)
        out.push({ wall: { ...wall, a: at(run.t0), b: at(t1) }, kind: run.kind });
    };
    for (let i = 0; i < cuts.length - 1; i++) {
      const t0 = cuts[i]!;
      const t1 = cuts[i + 1]!;
      const mid = (t0 + t1) / 2;
      const covering = marks.filter((m) => m.t0 <= mid && mid <= m.t1);
      const kind = covering.some((m) => m.kind === "door") ? "door" : covering.length ? "window" : "solid";
      if (!run || run.kind !== kind) {
        flush(t0);
        run = { t0, kind };
      }
    }
    flush(1);
  }
  return out;
}

/** Even-odd point-in-polygon. */
export function insidePolygon(p: Vec2, polygon: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** For each node, the index of the room outline it was captured in (or -1). */
export function roomsForNodes(nodes: Array<{ plan: Vec2 }>, rooms: PlanRoom[]): number[] {
  return nodes.map((n) => rooms.findIndex((r) => insidePolygon(n.plan, r.outline)));
}

/** A room's tag on the model: `Cocina · 9 m²` when the room was measured (LiDAR), else the name. */
export function roomTag(name: string, room: PlanRoom | undefined, unit: AreaUnit, lang: TourLanguage): string {
  return room?.areaSqm ? `${name} · ${formatArea(room.areaSqm, unit, lang)}` : name;
}
